/* eslint-disable react/no-array-index-key */
import React, { useEffect, useMemo, useState } from 'react';
import {
  StyleSheet,
  Dimensions,
  View,
  Image,
  ScrollView,
  TouchableOpacity,
  Text,
  Linking,
} from 'react-native';
import { ResizeMode } from 'expo-av';
import { useLocation } from 'react-router-dom';
import { RFValue } from 'react-native-responsive-fontsize';
import { appSettingsRepository } from '../../Domain/Repositories/AppSettingsRepository';
import PrimaryBackground from '../../Images/Icons/PrimaryBackground.jpg';
import { BLACK, CUBE_RED } from '../../Config/Color';
import Fonts from '../../Domain/Types/Fonts';
import UrbanPlaygroundIcon from '../../Images/Icons/playground.png';
import BulletTimePlaceholder from '../../Images/Icons/Cube.jpeg';
import LargeBookingViewModel from '../../Domain/Models/LargeBookingViewModel';
import LargeBookingService from '../../Domain/Services/LargeBookingService';
import { LargeBookingTokenKey } from '../../Config/Constant';

/**
 * Renders the Main Screen.
 * @returns JSX.Element.
 */
export default function LargeBookingScreen(): JSX.Element
{
  const [data, setData] = useState<LargeBookingViewModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { search } = useLocation<{ largeBooking: string}>();
  const [portrait, setPortrait] = useState(false);

  useEffect(() =>
  {
    // Expecting a url: http://localhost:19006/#/Cube1/LargeBooking?largebooking=1
    appSettingsRepository.dataRetrievalCompleted = {
      didComplete: true,
      failed: false,
    };

    setError('');

    const token = new URLSearchParams(search).get(LargeBookingTokenKey);
    if (token === null)
    {
      setError('Token is not provided!');
      return;
    }

    (async (): Promise<void> =>
    {
      setLoading(true);
      const response = await LargeBookingService.get(token);
      setLoading(false);

      if (!response.success)
      {
        setError(response.reason);
        return;
      }

      setData(response.data);
    })();
  }, []);

  const onDimensionsChanged = (): void =>
  {
    const dimensions = Dimensions.get('screen');

    if (dimensions.width > dimensions.height)
    {
      setPortrait(false);
      return;
    }

    setPortrait(true);
  };

  useEffect(() =>
  {
    Dimensions.addEventListener('change', (): void => onDimensionsChanged());

    return (): void =>
    {
      Dimensions.removeEventListener('change', (): void => onDimensionsChanged());
    };
  }, []);

  const onUrlPressed = (url: string): void =>
  {
    (async (): Promise<void> =>
    {
      await Linking.openURL(url);
    })();
  };

  const renderContent = useMemo(() =>
  {
    if (error.trim() !== '')
    {
      return (
        <View style={styles.itemsContainer}>
          <Text style={styles.errorLabel}>
            Error:
          </Text>

          <Text style={styles.errorLabel}>
            {error}
          </Text>
        </View>
      );
    }

    if (loading)
    {
      return (
        <View style={styles.itemsContainer}>
          <Text style={styles.loadingLabel}>
            Loading, please wait...
          </Text>
        </View>
      );
    }

    if (data === undefined || data.GroupLinks.length === 0)
    {
      return (
        <View style={styles.itemsContainer}>
          <Text style={styles.title}>
            {'No booking references available.\n\nPlease try the link again.\n\nIf the issue persists, please speak to a staff member.'}
          </Text>
        </View>
      );
    }

    const items = data.GroupLinks.map((x, index) => (
      <View
        key={`largeBookingItem-${x.TakeawayLink}-${x.GroupName}`}
        style={[styles.itemContainer, {
          backgroundColor: BLACK(index % 2 === 0 ? 0.5 : 0.25),
        }]}
      >
        <TouchableOpacity
          style={styles.itemButton}
          onPress={(): void => onUrlPressed(x.TakeawayLink)}
        >
          <Text style={styles.label}>
            {x.GroupName}
          </Text>
        </TouchableOpacity>
      </View>
    ));

    return (
      <View style={styles.itemsContainer}>
        <View style={styles.titleContainer}>
          <Text style={styles.title}>
            Booking Reference
          </Text>

          <Text style={styles.title}>
            {data.bookingId}
          </Text>
        </View>

        {items}
      </View>
    );
  }, [error, loading, data]);

  return (
    <View style={portrait ? styles.portrait : styles.landscape}>
      <View style={styles.background}>
        <Image
          source={PrimaryBackground}
          resizeMode={ResizeMode.COVER}
          style={styles.backgroundImage}
        />
      </View>

      <View style={styles.container}>
        <View style={styles.contentContainer}>
          <ScrollView style={styles.scrollView}>
            <View style={styles.logoContainer}>
              <Image
                source={UrbanPlaygroundIcon}
                resizeMode="contain"
                style={styles.logo}
              />
            </View>

            <View style={styles.cubeImageContainer}>
              <Image
                source={BulletTimePlaceholder}
                resizeMode={ResizeMode.COVER}
                style={styles.cubeImage}
              />
            </View>

            <View style={styles.primaryContentContainer}>
              {renderContent}
            </View>
          </ScrollView>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  landscape: {
    flex: 1,
    height: appSettingsRepository.appWidth,
    minWidth: appSettingsRepository.appHeight,
  },
  portrait: {
    flex: 1,
    height: appSettingsRepository.appHeight,
    minWidth: appSettingsRepository.appWidth,
  },
  background: {
    position: 'absolute',
    top: 0,
    zIndex: 0,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    overflow: 'hidden',
  },
  backgroundImage: {
    minWidth: '100%',
    height: '100%',
    aspectRatio: 0.653,
    overflow: 'hidden',
  },
  container: {
    backgroundColor: CUBE_RED(0.9),
    width: '100%',
    height: '100%',
  },
  contentContainer: {
    width: '100%',
    height: '100%',
  },
  scrollView: {
    width: '100%',
    height: '100%',
  },
  logoContainer: {
    width: '100%',
    height: RFValue(40),
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: RFValue(190),
    aspectRatio: 2880 / 196,
  },
  cubeImageContainer: {
    width: '100%',
    height: RFValue(150),
    overflow: 'hidden',
  },
  cubeImage: {
    width: '100%',
    height: '100%',
  },
  primaryContentContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorLabel: {
    fontSize: RFValue(14),
    textTransform: 'uppercase',
    fontFamily: Fonts.DDinCondensed,
    width: '80%',
    color: 'white',
    textAlign: 'center',
    marginVertical: RFValue(10),
  },
  loadingLabel: {
    fontSize: RFValue(14),
    textTransform: 'uppercase',
    fontFamily: Fonts.DDinCondensed,
    width: '80%',
    color: 'white',
    textAlign: 'center',
    marginVertical: RFValue(10),
  },
  titleContainer: {
    width: '100%',
  },
  title: {
    fontSize: RFValue(16),
    textTransform: 'uppercase',
    fontFamily: Fonts.DDinCondensedBold,
    color: 'white',
    textAlign: 'center',
    marginVertical: RFValue(10),
  },
  itemsContainer: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemContainer: {
    width: '100%',
    height: RFValue(40),
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemButton: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  label: {
    fontSize: RFValue(13),
    textAlignVertical: 'center',
    textTransform: 'uppercase',
    alignSelf: 'center',
    fontFamily: Fonts.KlaptMedium,
    color: 'white',
    zIndex: 1,
    width: '100%',
    textAlign: 'center',
  },
});
